import React from "react";
import {
  Box,
  Paper,
  Stack,
  Button,
  Typography,
  Tooltip,
  Divider,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MaterialReactTable } from "material-react-table";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { dictToArray } from "../utils/tableHelpers";

/* ─────────────────────────────────────────────────────────────
   TablePanel — a single labelled table card
───────────────────────────────────────────────────────────── */
function TablePanel({
  label,
  columns,
  dict,
  diffMap,
  compareMode,
  onRemove,
  onRowSelect,
  onEdit,
  selectedRowId,
}) {
  const theme    = useTheme();
  const rows     = dictToArray(dict);
  const rowCount = rows.length;
  const diffCount = compareMode ? Object.keys(diffMap || {}).length : 0;
  const yellowMain = "#FFD600";

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: `0 2px 14px ${alpha(theme.palette.primary.main, 0.14)}`,
        },
      }}
    >
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5, py: 0.85,
          backgroundColor: alpha(theme.palette.primary.main, 0.13),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
        }}
      >
        <Stack direction="row" alignItems="center" gap={0.75}>
          <TableChartOutlinedIcon
            sx={{ fontSize: 14, color: alpha(theme.palette.primary.main, 0.70) }}
          />
          <Typography variant="caption" sx={{
            fontWeight: 700, fontSize: "0.68rem", letterSpacing: "0.07em",
            textTransform: "uppercase", color: alpha(theme.palette.primary.main, 0.85),
          }}>
            {label}
          </Typography>

          {rowCount > 0 && (
            <Box sx={{
              px: 0.8, py: 0.05, borderRadius: "10px",
              backgroundColor: alpha(theme.palette.primary.main, 0.35),
              fontSize: "0.6rem", fontWeight: 700,
              color: theme.palette.primary.main, lineHeight: 1.7,
            }}>
              {rowCount} rows
            </Box>
          )}

          {diffCount > 0 && (
            <Stack direction="row" alignItems="center" gap={0.3} sx={{
              px: 0.8, py: 0.05, borderRadius: "10px",
              backgroundColor: alpha(yellowMain, 0.45), lineHeight: 1.7,
            }}>
              <ErrorOutlineIcon sx={{ fontSize: 10, color: yellowMain }} />
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#7A6000" }}>
                {diffCount} diff
              </Typography>
            </Stack>
          )}
        </Stack>

        <Tooltip title="Clear table">
          <Button size="small" variant="text" onClick={onRemove}
            sx={{
              minWidth: 28, height: 24, p: 0, borderRadius: 1,
              color: alpha(theme.palette.error.main, 0.45),
              "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.06),
                color: theme.palette.error.main },
            }}>
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </Button>
        </Tooltip>
      </Stack>

      {/* ── Body ── */}
      {rowCount === 0 ? (
        <Box sx={{
          py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
        }}>
          <TableChartOutlinedIcon sx={{ fontSize: 28, color: alpha(theme.palette.primary.main, 0.25) }} />
          <Typography variant="caption" sx={{ color: alpha(theme.palette.primary.main, 0.50), fontSize: "0.68rem" }}>
            No data — use Make List to load
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={rows}
          enableEditing={false}
          enableSorting={false}
          enableGlobalFilter={false}
          enableFilters={false}
          enablePagination={false}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableRowActions={false}
          enableColumnResizing={false}
          layoutMode="grid"
          muiTablePaperProps={{ elevation: 0, sx: { borderRadius: 0 } }}
          muiTableContainerProps={{
            sx: {
              maxHeight: 320,
              "&::-webkit-scrollbar": { width: 5, height: 5 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.35), borderRadius: 3,
              },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.07em",
              textTransform: "uppercase", color: alpha(theme.palette.primary.main, 0.75),
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
              borderBottom: `1.5px solid ${alpha(theme.palette.primary.main, 0.22)}`,
              py: 0.85, px: 1.25, whiteSpace: "nowrap",
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              fontSize: "0.73rem", py: 0.55, px: 1.25,
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.10)}`,
            },
          }}
          muiEditTextFieldProps={({ cell }) => ({
            autoFocus: true,
            onBlur: (e) => onEdit?.(cell.row.original._rowId, cell.column.id, e.target.value),
          })}
          muiTableBodyRowProps={({ row }) => {
            const isDiff    = compareMode && diffMap?.[row.original._rowId];
            const isSelected = selectedRowId === row.original._rowId;
            const yellowMain = "#FFD600";
            return {
              onClick: (e) => {
                if (e.target.tagName === "INPUT") return;
                onRowSelect?.(row.original._rowId, row.original);
              },
              sx: {
                cursor: onRowSelect ? "pointer" : "default",
                transition: "background-color 0.1s ease",
                "& td": {
                  backgroundColor: isSelected
                    ? alpha(theme.palette.primary.main, 0.14)
                    : isDiff
                    ? alpha(yellowMain, 0.45)
                    : undefined,
                },
                "& td:first-of-type": isDiff
                  ? { borderLeft: `3px solid ${yellowMain}` }
                  : isSelected
                  ? { borderLeft: `3px solid ${theme.palette.primary.main}` }
                  : {},
                "&:hover td": {
                  backgroundColor: isSelected
                    ? alpha(theme.palette.primary.main, 0.22)
                    : isDiff
                    ? alpha(yellowMain, 0.62)
                    : alpha(theme.palette.primary.main, 0.13),
                },
              },
            };
          }}
        />
      )}
    </Paper>
  );
}

/* ─────────────────────────────────────────────────────────────
   Divider between tables
───────────────────────────────────────────────────────────── */
function TableDivider({ compareMode }) {
  const theme = useTheme();
  const yellowMain = "#FFD600";
  const color = compareMode ? yellowMain : theme.palette.primary.main;
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ px: 0.5 }}>
      <Divider sx={{ flex: 1, borderColor: alpha(color, 0.28) }} />
      <Box sx={{
        px: 1.25, py: 0.3, borderRadius: "10px",
        border: `1px solid ${alpha(color, 0.45)}`,
        backgroundColor: alpha(color, 0.10),
        fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.06em",
        color: alpha(color, 0.90), whiteSpace: "nowrap",
      }}>
        {compareMode ? "COMPARING" : "VS"}
      </Box>
      <Divider sx={{ flex: 1, borderColor: alpha(color, 0.28) }} />
    </Stack>
  );
}

/* ─────────────────────────────────────────────────────────────
   DualBoxSection — exported
───────────────────────────────────────────────────────────── */
export default function DualBoxSection({
  leftDict      = {},
  rightDict     = {},
  leftColumns   = [],
  rightColumns  = [],
  onLeftEdit,
  onRightEdit,
  onLeftRowSelect,
  onRightRowSelect,
  selectedLeftRow,
  selectedRightRow,
  removeLeftTable,
  removeRightTable,
  compareMode   = false,
  leftDiffMap   = {},
  rightDiffMap  = {},
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%" }}>
      <TablePanel
        label="List A"
        columns={leftColumns}
        dict={leftDict}
        diffMap={leftDiffMap}
        compareMode={compareMode}
        onRemove={removeLeftTable}
        onRowSelect={onLeftRowSelect}
        onEdit={onLeftEdit}
        selectedRowId={selectedLeftRow}
      />

      <TableDivider compareMode={compareMode} />

      <TablePanel
        label="List B"
        columns={rightColumns}
        dict={rightDict}
        diffMap={rightDiffMap}
        compareMode={compareMode}
        onRemove={removeRightTable}
        onRowSelect={onRightRowSelect}
        onEdit={onRightEdit}
        selectedRowId={selectedRightRow}
      />
    </Box>
  );
}
